import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Select,
  MenuItem,
  Button,
  TextField,
  Grid,
  CircularProgress,
  Alert,
  Collapse,
  IconButton,
  Chip,
} from '@mui/material';
import {
  Send as SendIcon,
  ExpandMore as ExpandIcon,
  ExpandLess as CollapseIcon,
} from '@mui/icons-material';
import emailTemplatesApi, { EmailTemplate } from '../../services/email-templates-api';

interface Props {
  conversationId: number;
  onSent: () => void;
}

const EmailTemplateSuggestor: React.FC<Props> = ({ conversationId, onSent }) => {
  const [templates, setTemplates] = useState<EmailTemplate[]>([]);
  const [selectedTemplate, setSelectedTemplate] = useState<EmailTemplate | null>(null);
  const [customVariables, setCustomVariables] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [expanded, setExpanded] = useState(true);

  useEffect(() => {
    const fetchTemplates = async () => {
      try {
        const data = await emailTemplatesApi.list(true);
        setTemplates(data);
      } catch (err) {
        console.error('Failed to fetch templates:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchTemplates();
  }, []);

  const handleTemplateChange = (templateId: number) => {
    const template = templates.find((t) => t.id === templateId) || null;
    setSelectedTemplate(template);

    // Initialize only custom variables (system ones are auto-filled)
    if (template) {
      const customVars = (template.variables || []).filter(
        v => !v.startsWith('contact.') && !v.startsWith('account.') &&
             !v.startsWith('agent.') && !v.startsWith('conversation.') &&
             !v.startsWith('current_')
      );
      const initialVars: Record<string, string> = {};
      customVars.forEach((v) => {
        initialVars[v] = '';
      });
      setCustomVariables(initialVars);
    }
    setError(null);
    setSuccess(false);
  };

  const handleVariableChange = (name: string, value: string) => {
    setCustomVariables((prev) => ({ ...prev, [name]: value }));
  };

  const handleSend = async () => {
    if (!selectedTemplate) return;

    setSending(true);
    setError(null);

    try {
      const result = await emailTemplatesApi.send(selectedTemplate.id, {
        conversation_id: conversationId,
        variables: customVariables,
      });

      if (result.success) {
        setSuccess(true);
        setSelectedTemplate(null);
        setCustomVariables({});
        onSent();
      } else {
        setError(result.error || 'Failed to send email');
      }
    } catch (err: any) {
      setError(err.response?.data?.detail || err.message || 'Failed to send');
    } finally {
      setSending(false);
    }
  };

  // Get custom variables (not system variables)
  const customVariableNames = selectedTemplate
    ? (selectedTemplate.variables || []).filter(
        v => !v.startsWith('contact.') && !v.startsWith('account.') &&
             !v.startsWith('agent.') && !v.startsWith('conversation.') &&
             !v.startsWith('current_')
      )
    : [];

  if (loading) {
    return (
      <Box sx={{ p: 2, textAlign: 'center' }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (templates.length === 0) {
    return null;
  }

  return (
    <Paper sx={{ mb: 2 }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          p: 1.5,
          cursor: 'pointer',
          bgcolor: 'grey.50',
        }}
        onClick={() => setExpanded(!expanded)}
      >
        <Typography variant="subtitle2">Email Templates</Typography>
        <IconButton size="small">
          {expanded ? <CollapseIcon /> : <ExpandIcon />}
        </IconButton>
      </Box>

      <Collapse in={expanded}>
        <Box sx={{ p: 2 }}>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          {success && (
            <Alert severity="success" sx={{ mb: 2 }}>
              Email sent successfully!
            </Alert>
          )}

          <Select
            fullWidth
            size="small"
            value={selectedTemplate?.id || ''}
            onChange={(e) => handleTemplateChange(Number(e.target.value))}
            displayEmpty
            sx={{ mb: 2 }}
          >
            <MenuItem value="">Select a template...</MenuItem>
            {templates.map((t) => (
              <MenuItem key={t.id} value={t.id}>
                {t.name}
                {t.category && (
                  <Chip label={t.category} size="small" sx={{ ml: 1 }} />
                )}
              </MenuItem>
            ))}
          </Select>

          {selectedTemplate && (
            <>
              <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                System variables (contact, account, etc.) are auto-filled
              </Typography>

              {customVariableNames.length > 0 && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="caption" color="text.secondary" gutterBottom>
                    Fill in custom variables:
                  </Typography>
                  <Grid container spacing={1} sx={{ mt: 0.5 }}>
                    {customVariableNames.map((v) => (
                      <Grid item xs={6} key={v}>
                        <TextField
                          size="small"
                          fullWidth
                          label={v}
                          value={customVariables[v] || ''}
                          onChange={(e) => handleVariableChange(v, e.target.value)}
                        />
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              )}

              {selectedTemplate.attachments && selectedTemplate.attachments.length > 0 && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                  Includes {selectedTemplate.attachments.length} attachment(s)
                </Typography>
              )}

              <Button
                variant="contained"
                startIcon={sending ? <CircularProgress size={16} /> : <SendIcon />}
                onClick={handleSend}
                disabled={sending}
                fullWidth
              >
                {sending ? 'Sending...' : 'Send Email Template'}
              </Button>
            </>
          )}
        </Box>
      </Collapse>
    </Paper>
  );
};

export default EmailTemplateSuggestor;